import { useEffect, useState } from "react";
import { Download, Share, Plus, CheckCircle2, Smartphone, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";

const isStandalone = () =>
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

const isIOS = () =>
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

// Shared PWA install state — captures the deferred `beforeinstallprompt` event and
// exposes a single install trigger. Used by both the landing header button and card.
export function useInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(isStandalone());
    const [showIosHelp, setShowIosHelp] = useState(false);

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const promptInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
            return;
        }
        if (isIOS()) setShowIosHelp(true);
    };

    return { installed, showIosHelp, promptInstall, ios: isIOS() };
}

// Compact install button for the landing header.
export function HeaderInstallButton() {
    const { installed, promptInstall } = useInstallPrompt();
    if (installed) return null;
    return (
        <button
            type="button"
            data-testid="header-install-app-btn"
            onClick={promptInstall}
            title="Install the inmate app"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-phoenix-navy rounded-xl hover:bg-phoenix-soft/50 transition-colors"
        >
            <Download className="w-4 h-4 text-phoenix-orange" />
            <span>Install inmate app</span>
        </button>
    );
}

export function InstallAppCard() {
    const { installed, showIosHelp, promptInstall, ios } = useInstallPrompt();
    const handleInstall = promptInstall;

    return (
        <section data-testid="landing-get-app" className="bg-white border-y border-border/60">
            <div className="max-w-3xl mx-auto px-6 py-16">
                {/* Copy + actions */}
                <div className="space-y-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-phoenix-soft/60 rounded-full text-xs font-semibold text-phoenix-navy">
                        <Smartphone className="w-3.5 h-3.5 text-phoenix-orange" /> Installable app · Works on any device
                    </div>
                    <h2 className="font-display font-bold text-3xl text-phoenix-navy">Get the inmate app</h2>
                    <p className="text-base text-muted-foreground max-w-md">
                        Install Blue Phoenix to your home screen for one-tap access to mood check-ins,
                        journaling, skills, and your 24/7 AI companion. You'll sign in with your facility
                        access code each time — your information is private and never stored on the device.
                    </p>

                    <div className="flex flex-wrap items-center gap-3">
                        {installed ? (
                            <span
                                data-testid="app-installed-badge"
                                className="inline-flex items-center gap-2 px-5 py-3 bg-success/10 text-success rounded-2xl font-semibold text-sm"
                            >
                                <CheckCircle2 className="w-4.5 h-4.5" /> App installed
                            </span>
                        ) : (
                            <button
                                type="button"
                                data-testid="install-app-btn"
                                onClick={handleInstall}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-phoenix-navy text-white rounded-2xl font-semibold text-sm hover:bg-phoenix-navy-2 transition-colors"
                            >
                                <Download className="w-4.5 h-4.5" /> Install app
                            </button>
                        )}
                        <Link
                            to="/claim-access"
                            data-testid="get-app-claim-link"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-phoenix-navy/25 text-phoenix-navy rounded-2xl font-semibold text-sm hover:bg-phoenix-soft/40 transition-colors"
                        >
                            <KeyRound className="w-4.5 h-4.5" /> I have an access code
                        </Link>
                    </div>

                    {/* iOS / fallback instructions */}
                    {(showIosHelp || (ios && !installed)) && (
                        <div
                            data-testid="ios-install-help"
                            className="text-sm text-muted-foreground bg-secondary/40 border border-border rounded-xl p-4 space-y-1.5"
                        >
                            <p className="font-semibold text-phoenix-navy">On iPhone or iPad:</p>
                            <p className="flex items-center gap-1.5">
                                1. Tap the <Share className="w-4 h-4 inline text-phoenix-navy" /> Share button in Safari.
                            </p>
                            <p className="flex items-center gap-1.5">
                                2. Choose <Plus className="w-4 h-4 inline text-phoenix-navy" /> "Add to Home Screen".
                            </p>
                            <p>3. Tap "Add" — then open Blue Phoenix from your home screen.</p>
                        </div>
                    )}

                    {!installed && !ios && (
                        <p className="text-xs text-muted-foreground">
                            Tip: if you don't see an install prompt, open this page in Chrome, Edge, or
                            Safari and use your browser's "Install app" / "Add to Home Screen" option.
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}
